import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import ContentToolbar from '../components/ContentToolbar';
import { 
  Plus,
  Image,
  Edit,
  Trash2,
  MoreHorizontal,
  Facebook,
  Instagram,
  Upload,
  X,
  Film,
  Loader2,
  LayoutTemplate,
  Sparkles,
  Square,
  RectangleVertical,
  Video,
  Eye,
  EyeOff
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import TikTokIcon from '../components/icons/TikTokIcon';

const platformIcons = {
  facebook: Facebook,
  instagram: Instagram,
  tiktok: TikTokIcon,
};

const platformColors = {
  facebook: 'bg-blue-600',
  instagram: 'bg-gradient-to-r from-purple-500 to-pink-500',
  tiktok: 'bg-black',
};

const templateTypeIcons = {
  post: Square,
  story: RectangleVertical,
  reel: Video,
};

const templateTypeLabels = {
  post: 'Post',
  story: 'Story',
  reel: 'Reel',
};

const categoryOptions = [
  { value: 'promozioni', label: 'Promozioni' },
  { value: 'festivita', label: 'Festività' },
  { value: 'stagionali', label: 'Stagionali' },
  { value: 'novita', label: 'Novità' },
  { value: 'consigli', label: 'Consigli & Tips' },
  { value: 'team', label: 'Team & Staff' },
  { value: 'prima_dopo', label: 'Prima & Dopo' },
  { value: 'recensioni', label: 'Recensioni' },
  { value: 'generale', label: 'Generale' },
];

const CreatorPage = () => {
  const { API, getAuthHeaders, user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    platforms: [],
    template_type: 'post',
    category: 'generale',
    media_urls: [],
    is_active: true
  });

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/templates/admin`, getAuthHeaders());
      setTemplates(response.data);
    } catch (error) {
      console.error('Errore nel caricamento template:', error);
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        media_urls: formData.media_urls.map(m => typeof m === 'string' ? m : m.url)
      };
      
      if (selectedTemplate) {
        await axios.put(`${API}/templates/${selectedTemplate.id}`, submitData, getAuthHeaders());
      } else {
        await axios.post(`${API}/templates`, submitData, getAuthHeaders());
      }
      fetchTemplates();
      resetForm();
    } catch (error) {
      console.error('Errore nel salvataggio template:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo template?')) return;
    try {
      await axios.delete(`${API}/templates/${id}`, getAuthHeaders());
      fetchTemplates();
    } catch (error) {
      console.error('Errore nell\'eliminazione template:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const newMediaUrls = [...formData.media_urls];
    
    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(
          `${API}/upload/media`,
          formDataUpload,
          {
            ...getAuthHeaders(),
            headers: {
              ...getAuthHeaders().headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        newMediaUrls.push({
          id: response.data.id,
          url: response.data.url,
          filename: response.data.filename,
          media_type: response.data.media_type,
          thumbnail_url: response.data.thumbnail_url || null
        });
      } catch (error) {
        console.error('Errore upload file:', error);
        alert(`Errore nel caricamento di ${file.name}`);
      }
    }
    
    setFormData(prev => ({ ...prev, media_urls: newMediaUrls }));
    setUploading(false);
    e.target.value = '';
  };

  const removeMedia = async (index) => {
    const media = formData.media_urls[index];
    try {
      if (media.id && !media.id.startsWith('existing-')) {
        await axios.delete(`${API}/upload/${media.id}`, getAuthHeaders());
      }
    } catch (error) {
      console.error('Errore eliminazione file:', error);
    }
    
    setFormData(prev => ({
      ...prev,
      media_urls: prev.media_urls.filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      platforms: [],
      template_type: 'post',
      category: 'generale',
      media_urls: [],
      is_active: true
    });
    setSelectedTemplate(null);
    setIsDialogOpen(false);
  };

  const openEdit = (template) => {
    setSelectedTemplate(template);
    const mediaObjects = (template.media_urls || []).map((url, idx) => {
      if (typeof url === 'string') {
        const isVideo = url.match(/\.(mp4|mov|webm|avi)$/i);
        return {
          id: `existing-${idx}`,
          url: url,
          filename: url.split('/').pop(),
          media_type: isVideo ? 'video' : 'image'
        };
      }
      return url;
    });
    
    setFormData({
      title: template.title,
      content: template.content,
      platforms: template.platforms,
      template_type: template.template_type,
      category: template.category,
      media_urls: mediaObjects,
      is_active: template.is_active
    });
    setIsDialogOpen(true);
  };

  const togglePlatform = (platform) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const filteredTemplates = activeTab === 'all' 
    ? templates 
    : templates.filter(t => t.template_type === activeTab);

  const stats = {
    total: templates.length,
    post: templates.filter(t => t.template_type === 'post').length,
    story: templates.filter(t => t.template_type === 'story').length,
    reel: templates.filter(t => t.template_type === 'reel').length,
    active: templates.filter(t => t.is_active).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-outfit flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-primary" />
            Creator Studio
          </h1>
          <p className="text-muted-foreground mt-1">
            Crea modelli di post per tutti i centri estetici
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => resetForm()} data-testid="create-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Template
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-outfit">
                {selectedTemplate ? 'Modifica Template' : 'Crea Nuovo Template'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Titolo Template *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="es. Promo Estate 2024"
                  required
                  data-testid="template-title-input"
                />
              </div>

              {/* Template Type */}
              <div className="space-y-2">
                <Label>Tipo di Contenuto</Label>
                <div className="flex gap-3">
                  {['post', 'story', 'reel'].map((type) => {
                    const Icon = templateTypeIcons[type];
                    const isSelected = formData.template_type === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setFormData({...formData, template_type: type})}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                          isSelected 
                            ? 'border-primary bg-primary/10 text-primary' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`type-${type}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{templateTypeLabels[type]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Platforms */}
              <div className="space-y-2">
                <Label>Piattaforme</Label>
                <div className="flex gap-3">
                  {['facebook', 'instagram', 'tiktok'].map((platform) => {
                    const Icon = platformIcons[platform];
                    const isSelected = formData.platforms.includes(platform);
                    return (
                      <button
                        key={platform}
                        type="button"
                        onClick={() => togglePlatform(platform)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                          isSelected 
                            ? 'border-primary bg-primary/10 text-primary' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`platform-${platform}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm capitalize">{platform}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({...formData, category: value})}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Content */}
              <div className="space-y-2">
                <Label htmlFor="content">Contenuto / Caption</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder="Scrivi il testo del post... Usa {NOME_CENTRO} per personalizzazione"
                  rows={5}
                  data-testid="template-content-input"
                />
                <ContentToolbar
                  value={formData.content}
                  onChange={(val) => setFormData(prev => ({...prev, content: val}))}
                  platform="instagram"
                />
                <p className="text-xs text-muted-foreground">
                  Tip: Usa variabili come {'{NOME_CENTRO}'} per personalizzazione automatica
                </p>
              </div>

              {/* Media Upload */}
              <div className="space-y-3">
                <Label>Media (Immagini / Video)</Label>
                
                <div className="flex items-center gap-3">
                  <label 
                    htmlFor="template-media-upload"
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed cursor-pointer transition-all hover:border-primary hover:bg-primary/5 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {uploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    <span className="text-sm">{uploading ? 'Caricamento...' : 'Carica file'}</span>
                  </label>
                  <input
                    id="template-media-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
                    multiple
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                    data-testid="template-media-upload"
                  />
                  <span className="text-xs text-muted-foreground">
                    JPG, PNG, GIF, WebP, MP4, MOV (max 50MB)
                  </span>
                </div>

                {formData.media_urls.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {formData.media_urls.map((media, index) => (
                      <div key={media.url || `media-${index}`} className="relative group rounded-lg overflow-hidden border bg-muted aspect-square">
                        {media.media_type === 'video' ? (
                          media.thumbnail_url ? (
                            <>
                              <img src={`${API.replace('/api', '')}${media.thumbnail_url}`} alt="" className="w-full h-full object-cover" />
                              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                <Film className="w-6 h-6 text-white drop-shadow" />
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-black/10">
                              <Film className="w-8 h-8 text-muted-foreground" />
                            </div>
                          )
                        ) : (
                          <img
                            src={`${API.replace('/api', '')}${media.url}`}
                            alt={media.filename}
                            className="w-full h-full object-cover"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => removeMedia(index)}
                          className="absolute top-1 right-1 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <Label>Template Attivo</Label>
                  <p className="text-xs text-muted-foreground">I template attivi sono visibili a tutti gli utenti</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  data-testid="template-active-switch"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm}>Annulla</Button>
                <Button type="submit" data-testid="save-template-btn">
                  {selectedTemplate ? 'Aggiorna' : 'Crea'} Template
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="card-hover" onClick={() => setActiveTab('all')}>
          <CardContent className="p-4 cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <LayoutTemplate className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold font-outfit">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Totale</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover" onClick={() => setActiveTab('post')}>
          <CardContent className="p-4 cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Square className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold font-outfit">{stats.post}</p>
                <p className="text-xs text-muted-foreground">Post</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover" onClick={() => setActiveTab('story')}>
          <CardContent className="p-4 cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pink-500/10 flex items-center justify-center">
                <RectangleVertical className="w-5 h-5 text-pink-500" />
              </div>
              <div>
                <p className="text-2xl font-bold font-outfit">{stats.story}</p>
                <p className="text-xs text-muted-foreground">Stories</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover" onClick={() => setActiveTab('reel')}>
          <CardContent className="p-4 cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Video className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold font-outfit">{stats.reel}</p>
                <p className="text-xs text-muted-foreground">Reels</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Eye className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold font-outfit">{stats.active}</p>
                <p className="text-xs text-muted-foreground">Attivi</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Templates List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-outfit">
              {activeTab === 'all' ? 'Tutti i Template' : `Template ${templateTypeLabels[activeTab]}`}
            </CardTitle>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">Tutti</TabsTrigger>
                <TabsTrigger value="post">Post</TabsTrigger>
                <TabsTrigger value="story">Stories</TabsTrigger>
                <TabsTrigger value="reel">Reels</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {filteredTemplates.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map((template, index) => {
                const TypeIcon = templateTypeIcons[template.template_type];
                return (
                  <div 
                    key={template.id}
                    className={`rounded-lg border overflow-hidden transition-all hover:shadow-lg animate-fade-in ${!template.is_active ? 'opacity-60' : ''}`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`template-${template.id}`}
                  >
                    {/* Media Preview */}
                    {template.media_urls && template.media_urls.length > 0 ? (
                      <div className="aspect-video bg-muted relative">
                        {(() => {
                          const mediaUrl = template.media_urls[0];
                          const isVideo = template.media_types?.[0] === 'video'
                            || (typeof mediaUrl === 'string' && mediaUrl.match(/\.(mp4|mov|webm|avi)$/i));
                          const thumbUrl = template.thumbnails?.[0];
                          if (isVideo && thumbUrl) {
                            return (
                              <>
                                <img src={`${API.replace('/api', '')}${thumbUrl}`} alt="" className="w-full h-full object-cover" />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                  <Film className="w-10 h-10 text-white drop-shadow" />
                                </div>
                              </>
                            );
                          }
                          return isVideo ? (
                            <div className="w-full h-full flex items-center justify-center bg-black/10">
                              <Film className="w-12 h-12 text-muted-foreground" />
                            </div>
                          ) : (
                            <img
                              src={`${API.replace('/api', '')}${mediaUrl}`}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          );
                        })()}
                        {template.media_urls.length > 1 && (
                          <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                            +{template.media_urls.length - 1}
                          </div>
                        )}
                        {!template.is_active && (
                          <div className="absolute top-2 left-2">
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <EyeOff className="w-3 h-3" />
                              Nascosto
                            </Badge>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="aspect-video bg-muted flex items-center justify-center">
                        <Image className="w-12 h-12 text-muted-foreground/30" />
                      </div>
                    )}
                    
                    {/* Content */}
                    <div className="p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold line-clamp-1">{template.title}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="flex items-center gap-1">
                              <TypeIcon className="w-3 h-3" />
                              {templateTypeLabels[template.template_type]}
                            </Badge>
                            <Badge variant="secondary">
                              {categoryOptions.find(c => c.value === template.category)?.label || template.category}
                            </Badge>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(template)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Modifica
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(template.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {template.content || 'Nessuna descrizione'}
                      </p>
                      
                      <div className="flex items-center gap-2">
                        {template.platforms.map((platform) => {
                          const Icon = platformIcons[platform];
                          return (
                            <div 
                              key={platform}
                              className={`w-6 h-6 rounded-md ${platformColors[platform]} flex items-center justify-center`}
                            >
                              <Icon className="w-3.5 h-3.5 text-white" />
                            </div>
                          );
                        })}
                        <span className="text-xs text-muted-foreground ml-auto">
                          {format(parseISO(template.created_at), 'd MMM yyyy', { locale: it })}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <LayoutTemplate className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">Nessun template trovato</p>
              <p className="text-sm text-muted-foreground mt-1">
                Crea il tuo primo template per i centri estetici
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CreatorPage;
